import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import styled from "styled-components";
import { useHlsPlayer } from "../../../../../hooks/useHlsPlayer";

// NOTE: streams straight from videodelivery.net (not the API's
// customer-<code>.cloudflarestream.com proxy) — pre-existing behavior.
const toHls = (id: string) =>
  `https://videodelivery.net/${id}/manifest/video.m3u8`;
const toPoster = (id: string) =>
  `https://videodelivery.net/${id}/thumbnails/thumbnail.jpg`;

interface StoriesOnboardingProps {
  /** Cloudflare Stream video ids, from the per-project config (src/projects). */
  videoIds: string[];
}

const StoriesOnboarding: React.FC<StoriesOnboardingProps> = ({ videoIds }) => {
  const [idx, setIdx] = useState(0); // current story index
  const [muted, setMuted] = useState(true); // story-wide mute toggle
  const [isPaused, setIsPaused] = useState(false); // pause (press & hold or tab hidden)
  const stories = useMemo(
    () => videoIds.map((videoId, i) => ({ id: `s${i + 1}`, videoId })),
    [videoIds]
  );
  const current = stories[idx];
  const next = stories[idx + 1];

  // players
  const currentSrc = useMemo(() => toHls(current.videoId), [current.videoId]);
  const nextSrc = useMemo(
    () => (next ? toHls(next.videoId) : undefined),
    [next?.videoId]
  );

  const {
    videoRef: currentRef,
    ready: currentReady,
    hasFrame: currentHasFrame,
  } = useHlsPlayer({
    src: currentSrc,
    active: true,
    muted,
    shouldPlay: !isPaused, // pause/play all stories
  });

  // Preload the next story: attach HLS and buffer, but don't play
  const { videoRef: nextRef } = useHlsPlayer({
    src: nextSrc,
    active: !!nextSrc,
    muted: true, // keep muted for preload
    shouldPlay: false, // do NOT play, just prepare
  });

  // Auto-advance on ended (no loop)
  useEffect(() => {
    const el = currentRef.current;
    if (!el) return;
    const onEnded = () => goNext();
    el.loop = false;
    el.addEventListener("ended", onEnded);
    return () => el.removeEventListener("ended", onEnded);
  }, [currentRef, idx]);

  const goNext = useCallback(() => {
    setIdx((i) => (i + 1 < stories.length ? i + 1 : i)); // stop at last; or wrap with % stories.length
  }, []);
  const goPrev = useCallback(() => {
    setIdx((i) => (i - 1 >= 0 ? i - 1 : 0));
  }, []);

  // Keyboard navigation
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "ArrowRight") goNext();
      if (e.key === "ArrowLeft") goPrev();
      if (e.key === " ") setIsPaused((p) => !p);
      if (e.key.toLowerCase() === "m") setMuted((m) => !m);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [goNext, goPrev]);

  // Pause when tab hidden; resume when visible (if not manually paused)
  const manuallyPausedRef = useRef(false);
  useEffect(() => {
    const onVis = () => {
      if (document.visibilityState === "hidden") {
        manuallyPausedRef.current = isPaused;
        setIsPaused(true);
      } else {
        setIsPaused(manuallyPausedRef.current);
      }
    };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, [isPaused]);

  // Tap areas (left / right); press & hold to pause
  const holdTimer = useRef<number | null>(null);
  const onPressStart = () => {
    setIsPaused(true);
  };
  const onPressEnd = () => {
    setIsPaused(false);
  };

  // Simple swipe detection
  const startX = useRef<number | null>(null);
  const onTouchStart = (e: React.TouchEvent) => {
    startX.current = e.touches[0].clientX;
  };
  const onTouchEnd = (e: React.TouchEvent) => {
    if (startX.current == null) return;
    const dx = e.changedTouches[0].clientX - startX.current;
    if (Math.abs(dx) > 40) {
      if (dx < 0) goNext();
      else goPrev();
    }
    startX.current = null;
  };

  // Progress bar (based on currentTime / duration)
  const [progresses, setProgresses] = useState<number[]>(stories.map(() => 0));
  useEffect(() => {
    let raf = 0;
    const tick = () => {
      const el = currentRef.current;
      if (el && el.duration && isFinite(el.duration) && el.duration > 0) {
        const p = Math.min(1, (el.currentTime || 0) / el.duration);
        setProgresses((arr) =>
          arr.map((v, i) => (i < idx ? 1 : i === idx ? p : 0))
        );
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [currentRef, idx]);

  return (
    <Wrapper onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
      {/* Videos: current on top, next underneath (preloaded) */}
      {/* Preloaded next (hidden) */}
      {next && (
        <HiddenVideo
          ref={nextRef}
          poster={toPoster(next.videoId)}
          playsInline
          muted
          preload="auto"
          aria-hidden
        />
      )}

      {/* Current */}
      <VideoEl
        ref={currentRef}
        poster={toPoster(current.videoId)}
        playsInline
        autoPlay
        muted={muted}
        preload="auto"
      />

      {/* UI overlay */}
      <TopBar>
        <ProgressRow>
          {stories.map((s, i) => (
            <ProgressOuter key={s.id}>
              <ProgressInner
                style={{ transform: `scaleX(${progresses[i] || 0})` }}
              />
            </ProgressOuter>
          ))}
        </ProgressRow>

        <ControlsRight>
          <IconButton
            onClick={() => setMuted((m) => !m)}
            title={muted ? "Unmute (M)" : "Mute (M)"}
          >
            {muted ? "🔇" : "🔊"}
          </IconButton>
          {/* <IconButton onClick={() => setIsPaused((p) => !p)} title={isPaused ? "Play (Space)" : "Pause (Space)"}>
            {isPaused ? "▶️" : "⏸️"}
          </IconButton> */}
        </ControlsRight>
      </TopBar>

      {/* Tap zones */}
      <TapZoneLeft
        onMouseDown={onPressStart}
        onMouseUp={onPressEnd}
        onMouseLeave={onPressEnd}
        onTouchStart={onPressStart}
        onTouchEnd={onPressEnd}
        onClick={goPrev}
        aria-label="Previous story"
      />
      <TapZoneRight
        onMouseDown={onPressStart}
        onMouseUp={onPressEnd}
        onMouseLeave={onPressEnd}
        onTouchStart={onPressStart}
        onTouchEnd={onPressEnd}
        onClick={goNext}
        aria-label="Next story"
      />

      {/* Optional little status/help */}
      {/* <Debug>{currentReady ? "ready" : "loading"} • {currentHasFrame ? "frame" : "no frame"}</Debug> */}
    </Wrapper>
  );
};

export default StoriesOnboarding;

/* =================== styles =================== */

const Wrapper = styled.div`
  position: fixed;
  inset: 0;
  background: #000;
  overflow: hidden;
`;

const VideoEl = styled.video`
  position: absolute;
  inset: 0;
  height: 100dvh;
  background: #000;
  justify-self: center;
  @media (max-width: 600px) and (orientation: portrait) {
    object-fit: cover;
    width: 100vw;
  }
`;

const HiddenVideo = styled.video`
  position: absolute;
  inset: 0;
  width: 1px;
  height: 1px;
  opacity: 0;
  pointer-events: none;
`;

/* Top overlay */
const TopBar = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  padding: 10px 12px 6px;
  display: grid;
  grid-template-columns: 1fr auto;
  grid-template-rows: auto auto;
  gap: 8px 10px;
  pointer-events: none;
  z-index: 2;
`;

const ProgressRow = styled.div`
  grid-column: 1 / -1;
  display: grid;
  grid-auto-flow: column;
  gap: 6px;
  pointer-events: none;
`;

const ProgressOuter = styled.div`
  height: 3px;
  border-radius: 3px;
  overflow: hidden;
  background: rgba(255, 255, 255, 0.35);
  transform: translateZ(0);
`;

const ProgressInner = styled.div`
  height: 100%;
  background: rgba(255, 255, 255, 0.95);
  transform-origin: left center;
  transform: scaleX(0);
`;

const ControlsLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  pointer-events: auto;
`;
const ControlsRight = styled.div`
  justify-self: end;
  display: flex;
  align-items: center;
  gap: 8px;
  pointer-events: auto;
`;

const Badge = styled.div`
  color: #fff;
  font-weight: 600;
  font-size: 14px;
  background: rgba(0, 0, 0, 0.45);
  padding: 5px 8px;
  border-radius: 6px;
`;

const IconButton = styled.button`
  appearance: none;
  border: 0;
  border-radius: 8px;
  padding: 6px 8px;
  background: rgba(0, 0, 0, 0.45);
  color: #fff;
  font-size: 25px;
  cursor: pointer;
`;

/* Tap zones for prev/next; press & hold pauses */
const TapZoneBase = styled.button`
  position: absolute;
  top: 0;
  bottom: 0;
  width: 50%;
  background: transparent;
  border: 0;
  margin: 0;
  padding: 0;
  cursor: pointer;
  z-index: 1;
  outline: none;
  /* make them “invisible” clickable layers */
`;
const TapZoneLeft = styled(TapZoneBase)`
  left: 0;
`;
const TapZoneRight = styled(TapZoneBase)`
  right: 0;
`;

/* const Debug = styled.div`
  position: absolute; bottom: 10px; right: 10px;
  background: rgba(0,0,0,.5); color: #fff; padding: 6px 10px; border-radius: 6px; font-size: 12px; z-index: 2;
`; */
